import { useToastStore } from '@/stores/toast';
import Decimal from 'decimal.js';

export function handleError(error) {
  const toast = useToastStore();
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: error.message,
    life: 3000,
  });
}

export const formatTemplateValue = (value) => {
  if (value && typeof value === 'object' && 'value' in value) {
    return value.value;
  }
  return value;
};

export const parseWebVtt = (vttData) => {
  if (!vttData) {
    return [];
  }

  const lines = vttData.trim().split(/\r?\n/);

  if (lines.length > 0 && lines[0].trim() === 'WEBVTT') {
    lines.shift();
  }

  const parsedData = [];
  let cue = {};

  for (const line of lines) {
    if (!line.trim()) {
      continue;
    }

    if (line.includes('-->')) {
      const [startTime, endTime] = line.split(' --> ');
      cue.startTime = formatWebVttTime(startTime);
      cue.endTime = formatWebVttTime(endTime);
    } else {
      cue.text = cue.text ? `${cue.text}\n${line}` : line;
    }

    if (cue.startTime && cue.endTime && cue.text) {
      parsedData.push(cue);
      cue = {};
    }
  }

  return parsedData;
};

export const download = (content, fileName, contentType) => {
  const a = document.createElement('a');
  const file = new Blob([content], { type: contentType });
  a.href = URL.createObjectURL(file);
  a.download = fileName;
  a.click();
};

export const downloadURL = (url, target) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = true;
  if (target) {
    a.target = target;
  }
  a.click();
};

function formatWebVttTime(webVttTime) {
  const seconds = parseInt(webVttTime.split(':')[1]);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedTime = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;

  return formattedTime;
}

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

export function getSecondsDifference(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.abs((end - start) / 1000);
}

export const secondsToHours = (decimalSeconds) => {
  const totalSeconds = Math.floor(decimalSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  let result = '';
  if (hours === 1) {
    result += `${String(hours)} hour,`;
  } else if (hours > 1) {
    result += `${String(hours)} hours,`;
  }

  if (minutes === 1) {
    result += ` ${String(minutes)} minute,`;
  } else if (minutes > 1) {
    result += ` ${String(minutes)} minutes,`;
  }

  if (remainingSeconds === 1) {
    result += ` ${String(remainingSeconds)} second,`;
  } else if (remainingSeconds > 1) {
    result += ` ${String(remainingSeconds)} seconds,`;
  } else if (result === '') {
    result = '0 seconds,';
  }
  return result.slice(0, -1);
};

export const formatArn = (value) => {
  return value.split('/').slice(-1)[0];
};

export const formatArnShort = (value) => {
  return formatArn(value).split('-')[0];
};

export const getName = (user) => {
  const firstName =
    user.first_name !== null && user.first_name !== 'null'
      ? user.first_name
      : '';
  const lastName =
    user.last_name !== null && user.last_name !== 'null' ? user.last_name : '';
  const name = `${firstName} ${lastName}`.trim();
  const email = user.email;
  return name || email || 'N/A';
};

const lockRefreshIds = [];
export const lockRefresh = (id) => {
  lockRefreshIds.push(id);
  window.onbeforeunload = () =>
    lockRefreshIds.length
      ? 'You have unsaved changes in progress.  Are you sure you want to leave this page?'
      : undefined;
};
export const unlockRefresh = (id) => {
  const lockIdsToKeep = lockRefreshIds.filter((lockId) => lockId !== id);
  lockRefreshIds.splice(0, lockRefreshIds.length, ...lockIdsToKeep);
};

// getDuration function accepts a duration value in seconds and returns a formatted string in the format of HH:MM:SS
export function getDuration(duration) {
  if (
    duration === undefined ||
    duration === null ||
    duration === '' ||
    isNaN(duration) ||
    duration < 0
  ) {
    return '00:00:00';
  }
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
// getShortDuration returns the duration without leading zeros
export function getShortDuration(duration) {
  const paddedDuration = getDuration(duration);
  const noHours = paddedDuration.replace(/^00:/, '');
  const noMinutes = noHours.replace(/^00:/, '');
  const noLeadingZero = noMinutes.replace(/^0/, '');
  return noLeadingZero;
}
// getDurationPadding returns a string of zeros and colons that can be used to pad a duration string
export function getDurationPadding(durationString) {
  const paddingString = '00:00:00';
  return paddingString.slice(0, paddingString.length - durationString.length);
}

// Converts a time value from the format hh:mm:ss.ms to seconds
export function convertToSeconds(value) {
  if (!value) return 0;
  const [hours, minutes, seconds] = value.split(':').map(Number);
  return hours * 60 * 60 + minutes * 60 + seconds;
}

export const roundDecimals = (value, decimals = 2) => {
  if (value === undefined || value === null) return value;
  const DecimalExpNeg = Decimal.clone();
  DecimalExpNeg.set({
    toExpNeg: -20,
  });
  const decimalValue = new DecimalExpNeg(value);
  if (decimalValue.isNaN()) return value;
  return decimalValue.toDecimalPlaces(decimals).toString();
};

export const roundMoney = (value, decimalPlaces = 2) => {
  if (value === undefined || value === null) return value;
  const decimalValue = new Decimal(value);
  if (decimalValue.isNaN()) return value;
  const roundedDecimal = roundDecimals(value, decimalPlaces);
  // Ensure there are at least 2 digits after the decimal point
  const parts = roundedDecimal.split('.');
  if (parts.length === 2) {
    const decimalPart = parts[1].padEnd(decimalPlaces, '0');
    return `${parts[0]}.${decimalPart}`;
  }
  return `${roundedDecimal}.00`;
};

// Converts a number into a string with the letters, K, M, B, T
export const prettyCount = (value) => {
  if (value === undefined || value === null) return value;
  // ensure value is a number
  value = Number(value);

  // if the value is negative, convert it to positive and store the sign
  const sign = value < 0 ? '-' : '';
  value = Math.abs(value);

  // if value is less than 1000, return the value as a string with the sign
  if (value < 1000) return sign + roundDecimals(value, 0);
  value = Math.round(value);

  // Make the value an integer with the appropriate suffix
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const suffixNum = Math.min(
    Math.floor(Math.log10(value) / 3),
    suffixes.length - 1
  );

  const shortValue = roundDecimals(value / Math.pow(1000, suffixNum), 0);

  // Format the value with commas if needed
  const localeValue = shortValue
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return sign + localeValue + suffixes[suffixNum];
};
